import React, { useCallback, useEffect, useState, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useData } from "@/context/DataContext";

// Link Google configurabile (fallback su impostazioni sito)
const GOOGLE_REVIEWS_URL = "";

const GoogleG = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
  </svg>
);

function Stars({ n = 5 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${n} stelle su 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={16} className={i < n ? "text-amber-400 fill-amber-400" : "text-neutral-300"} />
      ))}
    </div>
  );
}

function ReviewCard({ r }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const el = textRef.current;
    if (el) setOverflowing(el.scrollHeight > el.clientHeight + 2);
  }, []);

  return (
    <article className="h-full flex flex-col rounded-2xl bg-white border border-neutral-200 shadow-sm p-6 sm:p-7" data-testid={`review-card-${r.id}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <Stars n={r.rating || 5} />
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-500"><GoogleG /> Recensione Google</span>
      </div>
      <p ref={textRef} className={`text-neutral-700 text-sm leading-relaxed ${expanded ? "" : "line-clamp-5"}`}>{r.testo}</p>
      {(overflowing || expanded) && (
        <button onClick={() => setExpanded((v) => !v)} className="mt-2 self-start text-sm font-semibold text-[#1f7a34] hover:underline" data-testid={`review-toggle-${r.id}`}>
          {expanded ? "Mostra meno" : "Leggi tutto"}
        </button>
      )}
      <div className="mt-auto pt-5 flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-[#16381d] text-[#7bc142] flex items-center justify-center font-display text-lg">{(r.autore || "?").charAt(0)}</span>
        <div>
          <p className="font-semibold text-neutral-900 text-sm">{r.autore}</p>
          <p className="text-xs text-neutral-400">{r.data}</p>
        </div>
      </div>
    </article>
  );
}

export default function ReviewsCarousel() {
  const { reviews, settings } = useData();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", slidesToScroll: 1 });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);
  const hovering = useRef(false);

  const onSelect = useCallback(() => { if (emblaApi) setSelected(emblaApi.selectedScrollSnap()); }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", () => { setSnaps(emblaApi.scrollSnapList()); onSelect(); });
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const id = setInterval(() => { if (!hovering.current && emblaApi) emblaApi.scrollNext(); }, 5000);
    return () => clearInterval(id);
  }, [emblaApi]);

  if (!reviews || reviews.length === 0) return null;
  const googleUrl = GOOGLE_REVIEWS_URL || settings?.google_business || "#";

  return (
    <div
      onMouseEnter={() => (hovering.current = true)}
      onMouseLeave={() => (hovering.current = false)}
      className="relative"
      data-testid="reviews-carousel"
    >
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex -ml-5">
          {reviews.map((r) => (
            <div key={r.id} className="pl-5 shrink-0 grow-0 basis-full sm:basis-1/2 lg:basis-1/3">
              <ReviewCard r={r} />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 flex items-center justify-center gap-4">
        <button onClick={() => emblaApi && emblaApi.scrollPrev()} aria-label="Recensione precedente" data-testid="reviews-prev"
                className="w-10 h-10 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-700 hover:border-[#1f7a34] hover:text-[#1f7a34] transition-colors">
          <ChevronLeft size={18} />
        </button>
        <div className="hidden sm:flex items-center gap-2 flex-wrap justify-center max-w-[280px]">
          {snaps.map((_, i) => (
            <button key={i} onClick={() => emblaApi && emblaApi.scrollTo(i)} aria-label={`Vai alla recensione ${i + 1}`}
                    className={`h-2 rounded-full transition-all ${i === selected ? "w-6 bg-[#1f7a34]" : "w-2 bg-neutral-300 hover:bg-neutral-400"}`} />
          ))}
        </div>
        <span className="sm:hidden text-sm font-medium text-neutral-500 tabular-nums" data-testid="reviews-counter">{selected + 1} / {snaps.length}</span>
        <button onClick={() => emblaApi && emblaApi.scrollNext()} aria-label="Recensione successiva" data-testid="reviews-next"
                className="w-10 h-10 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-700 hover:border-[#1f7a34] hover:text-[#1f7a34] transition-colors">
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="mt-10 text-center">
        <a href={googleUrl} target="_blank" rel="noreferrer" data-testid="google-reviews-link"
           className="inline-flex items-center gap-2.5 rounded-full bg-[#1f7a34] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#16381d] transition-colors">
          <GoogleG /> Guarda tutte le recensioni su Google
        </a>
      </div>
    </div>
  );
}
